/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback, useRef } from "react";
import "./css/StaffPage.css"; // reuse same CSS
import {
  markAttendance,
  bulkMarkAttendance,
  getMonthlyGrid,
  getDailyAttendance,
  getMonthlyReport,
} from "../api/attendanceApi";
import { getAllStaff } from "../api/staffApi";
import {
  FiSearch,
  FiX,
  FiUsers,
  FiUserCheck,
  FiUserX,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
  FiChevronLeft,
  FiChevronRight,
  FiCalendar,
  FiClock,
  FiBarChart2,
  FiEdit2,
  FiSave,
  FiGrid,
} from "react-icons/fi";
import AccessControl from "../components/AccessControl";

// ── Constants ─────────────────────────────────────────────────────────────
const ADMIN_PANEL_ROLES = ["SUPERADMIN", "ADMIN", "HR", "ACCOUNTS"];
const LIMIT = 50;

const STATUS_META = {
  PRESENT:  { label: "Present",  color: "#085041", bg: "#E1F5EE", dot: "#1d9e75" },
  ABSENT:   { label: "Absent",   color: "#A32D2D", bg: "#FCEBEB", dot: "#e53e3e" },
  HALF_DAY: { label: "Half Day", color: "#633806", bg: "#FAEEDA", dot: "#d97706" },
  LEAVE:    { label: "Leave",    color: "#3C3489", bg: "#EEEDFE", dot: "#7C3AED" },
  HOLIDAY:  { label: "Holiday",  color: "#0C447C", bg: "#E6F1FB", dot: "#3182ce" },
  WEEK_OFF: { label: "Week Off", color: "#444441", bg: "#F1EFE8", dot: "#718096" },
};

const STATUS_SHORT = {
  PRESENT: "P", ABSENT: "A", HALF_DAY: "½", LEAVE: "L", HOLIDAY: "HO", WEEK_OFF: "WO",
};

const LEAVE_TYPES = ["CASUAL", "SICK", "EARNED", "LOP"];

const STATUSES = Object.keys(STATUS_META);

// Get today as YYYY-MM-DD in IST
const todayIST = () =>
  new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });

// Get current month as YYYY-MM
const currentMonthIST = () => todayIST().slice(0, 7);

// All days in a given YYYY-MM month
const getDaysInMonth = (month) => {
  const [y, m] = month.split("-").map(Number);
  const count = new Date(y, m, 0).getDate();
  return Array.from({ length: count }, (_, i) => {
    const d = String(i + 1).padStart(2, "0");
    return `${month}-${d}`;
  });
};

// Day-of-week label
const dayLabel = (dateStr) => {
  const d = new Date(dateStr + "T00:00:00");
  return ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d.getDay()];
};

const isSunday = (dateStr) => new Date(dateStr + "T00:00:00").getDay() === 0;

// ── Leave-type auto-resolver ──────────────────────────────────────────────
// Rule: Each staff gets 1 CASUAL leave per month.
// When marking LEAVE, check if they've already used their CASUAL this month.
// If not used → CASUAL. If already used → LOP.
// `attendanceMap` is { date: { status, leaveType, ... } } for that staff in the month.
// `excludeDate` is the date being edited (so we don't count today's own existing record).
const resolveLeaveType = (attendanceMap = {}, excludeDate = null) => {
  const casualUsed = Object.entries(attendanceMap).some(
    ([date, rec]) =>
      date !== excludeDate &&
      rec.status === "LEAVE" &&
      rec.leaveType === "CASUAL"
  );
  return casualUsed ? "LOP" : "CASUAL";
};

export default function AttendancePage() {
  const adminData = localStorage.getItem("admin");
  const userData  = adminData ? JSON.parse(adminData) : null;
  const myRole    = userData?.role || "";

  // ── View mode: "daily" | "monthly" | "report" ────────────────────────
  const [viewMode, setViewMode] = useState("daily");

  // ── Common state ──────────────────────────────────────────────────────
  const [loading, setLoading]     = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast]         = useState({ show: false, message: "", type: "" });

  // ── Daily view state ──────────────────────────────────────────────────
  const [selectedDate, setSelectedDate] = useState(todayIST());
  const [dailyRecords, setDailyRecords] = useState([]);
  const [dailyTally, setDailyTally]    = useState({});
  const [dailyPagination, setDailyPagination] = useState({ currentPage: 1, totalPages: 1, totalCount: 0 });
  const [dailyPage, setDailyPage]      = useState(1);

  // ── Monthly view state (all staff grid) ──────────────────────────────
  const [selectedMonth, setSelectedMonth] = useState(currentMonthIST());
  const [monthlyGrid, setMonthlyGrid]     = useState([]); // [{ _id, name, role, department, attendance: { date: {...} } }]
  const [monthlySearch, setMonthlySearch] = useState("");
  const [editingCell, setEditingCell]     = useState(null); // { staffId, staffName, date }
  const [cellForm, setCellForm]           = useState({ status: "PRESENT", checkIn: "", checkOut: "", leaveType: "", notes: "" });

  // ── Report view state ─────────────────────────────────────────────────
  const [reportMonth, setReportMonth]   = useState(currentMonthIST());
  const [reportData, setReportData]     = useState([]);
  const [reportSearch, setReportSearch] = useState("");
  const debounceRef = useRef(null);

  // ── Staff list for daily bulk-mark ────────────────────────────────────
  const [staffList, setStaffList]     = useState([]);
  const [bulkForm, setBulkForm]       = useState({}); // { staffId: { status, checkIn, checkOut, leaveType } }
  const [bulkMode, setBulkMode]       = useState(false);
  const [staffSearch, setStaffSearch] = useState("");
  const [bulkAttMap, setBulkAttMap]   = useState({}); // { staffId: { date: {...} } } — month attendance for leave-type auto-resolve

  // ── Filters ───────────────────────────────────────────────────────────
  const [filterStatus, setFilterStatus] = useState("all");

  // ── Toast helper ──────────────────────────────────────────────────────
  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "" }), 3000);
  };

  // ── Fetch daily attendance ────────────────────────────────────────────
  const fetchDaily = useCallback(async (date, page = 1) => {
    setLoading(true);
    try {
      const res = await getDailyAttendance({ date, n_page: page, n_limit: LIMIT });
      if (res.success) {
        setDailyRecords(res.records || []);
        setDailyTally(res.tally || {});
        setDailyPagination(res.pagination || { currentPage: 1, totalPages: 1, totalCount: 0 });
      }
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  // ── Fetch monthly grid (all staff) ────────────────────────────────────
  const fetchMonthlyGrid = useCallback(async (month) => {
    setLoading(true);
    try {
      const res = await getMonthlyGrid({ month });
      if (res.success) setMonthlyGrid(res.staff || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  // ── Fetch monthly report (all staff) ─────────────────────────────────
  const fetchReport = useCallback(async (month) => {
    setLoading(true);
    try {
      const res = await getMonthlyReport({ month });
      if (res.success) setReportData(res.report || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  // ── Fetch staff list for bulk-mark ────────────────────────────────────
  const fetchStaff = useCallback(async (search = "") => {
    try {
      const res = await getAllStaff({ n_limit: 100, c_search_term: search });
      if (res.success) {
        setStaffList(res.staff || []);
        // Init bulkForm with PRESENT for all
        const init = {};
        (res.staff || []).forEach((s) => {
          init[s._id] = { status: "PRESENT", checkIn: "09:00", checkOut: "18:00", leaveType: "" };
        });
        setBulkForm(init);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (viewMode === "daily") fetchDaily(selectedDate, dailyPage);
  }, [viewMode, selectedDate, dailyPage]);

  useEffect(() => {
    if (viewMode === "monthly") fetchMonthlyGrid(selectedMonth);
  }, [viewMode, selectedMonth]);

  useEffect(() => {
    if (viewMode === "report") fetchReport(reportMonth);
  }, [viewMode, reportMonth]);

  // Fetch staff + monthly grid when bulk mode opens
  useEffect(() => {
    if (!bulkMode) return;
    fetchStaff(staffSearch);
    // Also fetch this month's attendance grid so we can auto-resolve leave types
    const month = selectedDate.slice(0, 7);
    (async () => {
      try {
        const res = await getMonthlyGrid({ month });
        if (res.success) {
          const map = {};
          (res.staff || []).forEach((s) => { map[s._id] = s.attendance || {}; });
          setBulkAttMap(map);
        }
      } catch { /* ignore */ }
    })();
  }, [bulkMode]);

  // ── Bulk mark submit ──────────────────────────────────────────────────
  const handleBulkSubmit = async () => {
    setSubmitting(true);
    try {
      const records = staffList.map((s) => ({
        staffId:   s._id,
        ...bulkForm[s._id],
      }));
      const res = await bulkMarkAttendance({ date: selectedDate, records });
      if (res.success) {
        showToast(`Attendance saved for ${res.upserted + res.modified} staff`);
        setBulkMode(false);
        fetchDaily(selectedDate, dailyPage);
      } else {
        showToast(res.error || "Failed to save", "error");
      }
    } catch { showToast("Failed to save", "error"); }
    finally { setSubmitting(false); }
  };

  // ── Single cell save (monthly grid) ────────────────────────────────────
  const handleCellSave = async () => {
    if (!editingCell) return;
    setSubmitting(true);
    try {
      const res = await markAttendance({
        staffId:   editingCell.staffId,
        date:      editingCell.date,
        status:    cellForm.status,
        checkIn:   cellForm.checkIn || null,
        checkOut:  cellForm.checkOut || null,
        leaveType: cellForm.status === "LEAVE" ? cellForm.leaveType : null,
        notes:     cellForm.notes || null,
      });
      if (res.success) {
        showToast("Attendance updated");
        setEditingCell(null);
        fetchMonthlyGrid(selectedMonth);
      } else {
        showToast(res.error || "Failed", "error");
      }
    } catch { showToast("Failed", "error"); }
    finally { setSubmitting(false); }
  };

  // ── Open cell editor ──────────────────────────────────────────────────
  const openCellEdit = (staffId, staffName, date) => {
    const staffData  = monthlyGrid.find((s) => s._id === staffId);
    const existing   = staffData?.attendance?.[date];
    const attMap     = staffData?.attendance || {};

    // Auto-resolve leave type: if opening fresh LEAVE, decide CASUAL vs LOP
    let defaultLeaveType = existing?.leaveType || "";
    if (!existing && isSunday(date)) {
      // default WEEK_OFF for sundays
    } else if (existing?.status === "LEAVE" && !existing?.leaveType) {
      defaultLeaveType = resolveLeaveType(attMap, date);
    }

    setCellForm({
      status:    existing?.status    || (isSunday(date) ? "WEEK_OFF" : "PRESENT"),
      checkIn:   existing?.checkIn   || "",
      checkOut:  existing?.checkOut  || "",
      leaveType: defaultLeaveType,
      notes:     existing?.notes     || "",
      _attMap:   attMap,   // stash for use in onChange
    });
    setEditingCell({ staffId, staffName, date });
  };

  // ── Sub-components ────────────────────────────────────────────────────
  const StatusBadge = ({ status }) => {
    const m = STATUS_META[status] || STATUS_META.ABSENT;
    return (
      <span className="badge" style={{ background: m.bg, color: m.color, fontWeight: 700 }}>
        <span style={{ width: 7, height: 7, borderRadius: "50%", background: m.dot, display: "inline-block", marginRight: 5 }} />
        {m.label}
      </span>
    );
  };

  const SummaryCard = ({ label, value, color, bg, icon }) => (
    <div className="stat-card" style={{ minWidth: 0 }}>
      <div className="stat-icon" style={{ background: bg }}>{icon}</div>
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={{ color }}>{value}</div>
    </div>
  );

  // ── Filtered daily records ────────────────────────────────────────────
  const filteredDaily = filterStatus === "all"
    ? dailyRecords
    : dailyRecords.filter((r) => r.status === filterStatus);

  // ── Filtered monthly staff ─────────────────────────────────────────────
  const filteredMonthlyStaff = monthlyGrid.filter((s) =>
    !monthlySearch || s.name?.toLowerCase().includes(monthlySearch.toLowerCase())
  );

  // ── Filtered report ───────────────────────────────────────────────────
  const filteredReport = reportData.filter((r) =>
    !reportSearch || r.staffName?.toLowerCase().includes(reportSearch.toLowerCase())
  );

  // ── Days in month grid ────────────────────────────────────────────────
  const daysInMonth = getDaysInMonth(selectedMonth);

  return (
    <div>
      <AccessControl allowedRoles={ADMIN_PANEL_ROLES} redirectTo="/suggestions" />

      {/* Toast */}
      {toast.show && (
        <div className={`sf-toast sf-toast-${toast.type}`}>
          <span>
            {toast.type === "success" ? <FiCheckCircle size={16} />
              : toast.type === "warning" ? <FiAlertCircle size={16} />
              : <FiXCircle size={16} />}
          </span>
          <span>{toast.message}</span>
        </div>
      )}

      {/* ── Page Header ───────────────────────────────────────────────── */}
      <div className="sf-page-header section">
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 600, color: "#1a1a1a", margin: 0 }}>
            Attendance Management
          </h2>
          <p style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
            Track daily attendance · monthly view · team reports
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {/* View mode toggle */}
          {[
            { key: "daily",   icon: <FiCalendar size={13} />,  label: "Daily" },
            { key: "monthly", icon: <FiGrid size={13} />,      label: "Monthly" },
            { key: "report",  icon: <FiBarChart2 size={13} />, label: "Report" },
          ].map(({ key, icon, label }) => (
            <button
              key={key}
              className={viewMode === key ? "btn btn-success" : "btn-outline-green"}
              style={{ fontSize: 12, padding: "6px 14px", display: "flex", alignItems: "center", gap: 5 }}
              onClick={() => setViewMode(key)}
            >
              {icon} {label}
            </button>
          ))}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          DAILY VIEW
      ══════════════════════════════════════════════════════════════════ */}
      {viewMode === "daily" && (
        <>
          {/* Stats row */}
          <div className="stat-grid section">
            <SummaryCard label="Present"  value={dailyTally.PRESENT  || 0} color="#085041" bg="#E1F5EE" icon={<FiUserCheck size={18} color="#0F6E56" />} />
            <SummaryCard label="Absent"   value={dailyTally.ABSENT   || 0} color="#A32D2D" bg="#FCEBEB" icon={<FiUserX    size={18} color="#A32D2D" />} />
            <SummaryCard label="Half Day" value={dailyTally.HALF_DAY || 0} color="#633806" bg="#FAEEDA" icon={<FiClock    size={18} color="#854F0B" />} />
            <SummaryCard label="Leave"    value={dailyTally.LEAVE    || 0} color="#3C3489" bg="#EEEDFE" icon={<FiCalendar size={18} color="#7C3AED" />} />
          </div>

          {/* Toolbar */}
          <div className="card section">
            <div className="sf-toolbar">
              <div className="filter-tabs">
                {["all", ...STATUSES].map((s) => (
                  <button
                    key={s}
                    className={`filter-tab ${filterStatus === s ? "active" : ""}`}
                    onClick={() => setFilterStatus(s)}
                  >
                    {s === "all" ? <><FiUsers size={12} /> All</> : STATUS_META[s]?.label || s}
                  </button>
                ))}
              </div>
              <div className="sf-toolbar-right">
                {/* Date picker */}
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <FiCalendar size={14} color="#888" />
                  <input
                    type="date"
                    value={selectedDate}
                    max={todayIST()}
                    style={{ border: "1.5px solid #e5e7eb", borderRadius: 8, padding: "6px 10px", fontSize: 13, color: "#333" }}
                    onChange={(e) => { setSelectedDate(e.target.value); setDailyPage(1); }}
                  />
                </div>
                {/* Bulk mark button */}
                {["SUPERADMIN", "ADMIN", "HR"].includes(myRole) && (
                  <button
                    className="btn btn-success"
                    style={{ fontSize: 12 }}
                    onClick={() => setBulkMode(true)}
                  >
                    <FiEdit2 size={13} /> Bulk Mark
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Daily Records Table */}
          <div className="card section">
            {loading ? (
              <div className="sf-loading" style={{ height: "35vh" }}>
                <div className="spinner" style={{ width: 24, height: 24, borderColor: "#1d9e75", borderTopColor: "transparent" }} />
                <p style={{ marginTop: 12, color: "#888", fontSize: 13 }}>Loading attendance…</p>
              </div>
            ) : filteredDaily.length === 0 ? (
              <div className="sf-empty">
                <FiCalendar size={40} color="#ddd" />
                <p>No attendance records for {selectedDate}</p>
                <p style={{ fontSize: 12, color: "#bbb" }}>Use "Bulk Mark" to add today's attendance</p>
              </div>
            ) : (
              <div className="table-wrapper">
                <table className="sf-table">
                  <thead>
                    <tr>
                      <th>Staff</th>
                      <th>Status</th>
                      <th>Check In</th>
                      <th>Check Out</th>
                      <th>Hours</th>
                      <th>Leave Type</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDaily.map((rec) => (
                      <tr key={rec._id}>
                        <td>
                          <div className="sf-member-cell">
                            <div className="sf-avatar">{rec.staffName?.charAt(0)?.toUpperCase()}</div>
                            <div>
                              <div className="sf-member-name">{rec.staffName}</div>
                              <div className="sf-member-email">{rec.staffRole}</div>
                            </div>
                          </div>
                        </td>
                        <td><StatusBadge status={rec.status} /></td>
                        <td>
                          <span style={{ fontSize: 12, color: "#555", fontFamily: "monospace" }}>
                            {rec.checkIn || <span style={{ color: "#ccc" }}>—</span>}
                          </span>
                        </td>
                        <td>
                          <span style={{ fontSize: 12, color: "#555", fontFamily: "monospace" }}>
                            {rec.checkOut || <span style={{ color: "#ccc" }}>—</span>}
                          </span>
                        </td>
                        <td>
                          <span style={{ fontSize: 12, fontWeight: 600, color: rec.workHours > 0 ? "#085041" : "#ccc" }}>
                            {rec.workHours > 0 ? `${rec.workHours}h` : "—"}
                          </span>
                        </td>
                        <td>
                          {rec.leaveType
                            ? <span className="badge" style={{ background: "#EEEDFE", color: "#3C3489", fontWeight: 700 }}>{rec.leaveType}</span>
                            : <span style={{ color: "#ccc", fontSize: 12 }}>—</span>}
                        </td>
                        <td>
                          <span style={{ fontSize: 12, color: "#888" }}>
                            {rec.notes || <span style={{ color: "#ddd" }}>—</span>}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {dailyPagination.totalPages > 1 && (
              <div className="sf-pagination">
                <button className="sf-page-btn" disabled={dailyPage <= 1} onClick={() => setDailyPage((p) => p - 1)}>
                  <FiChevronLeft size={14} />
                </button>
                <button className="sf-page-btn" disabled={dailyPage >= dailyPagination.totalPages} onClick={() => setDailyPage((p) => p + 1)}>
                  <FiChevronRight size={14} />
                </button>
                <span className="sf-page-info">
                  {dailyPage} of {dailyPagination.totalPages} · {dailyPagination.totalCount} total
                </span>
              </div>
            )}
          </div>
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          MONTHLY VIEW — attendance grid for all staff
      ══════════════════════════════════════════════════════════════════ */}
      {viewMode === "monthly" && (
        <>
          {/* Controls */}
          <div className="card section">
            <div className="sf-toolbar" style={{ flexWrap: "wrap", gap: 12 }}>
              {/* Staff search */}
              <div className="search-box" style={{ flex: 1, minWidth: 200 }}>
                <FiSearch size={14} className="search-icon" />
                <input
                  placeholder="Search staff…"
                  value={monthlySearch}
                  onChange={(e) => setMonthlySearch(e.target.value)}
                />
                {monthlySearch && (
                  <button className="search-clear" onClick={() => setMonthlySearch("")}><FiX size={12} /></button>
                )}
              </div>

              {/* Month picker */}
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <FiCalendar size={14} color="#888" />
                <input
                  type="month"
                  value={selectedMonth}
                  max={currentMonthIST()}
                  style={{ border: "1.5px solid #e5e7eb", borderRadius: 8, padding: "6px 10px", fontSize: 13, color: "#333" }}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Legend */}
          <div className="card section" style={{ padding: "10px 16px" }}>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                Legend
              </span>
              {Object.entries(STATUS_META).map(([k, v]) => (
                <span key={k} style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 5, color: v.color, fontWeight: 600 }}>
                  <span style={{ width: 9, height: 9, borderRadius: "50%", background: v.dot, display: "inline-block" }} />
                  {v.label}
                </span>
              ))}
              <span style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 5, color: "#A32D2D", fontWeight: 600 }}>
                <span style={{ width: 9, height: 9, borderRadius: "50%", background: "#e53e3e", display: "inline-block", opacity: 0.35 }} />
                Not Marked
              </span>
            </div>
          </div>

          {/* Attendance Grid */}
          <div className="card section">
            {loading ? (
              <div className="sf-loading" style={{ height: "35vh" }}>
                <div className="spinner" style={{ width: 24, height: 24, borderColor: "#1d9e75", borderTopColor: "transparent" }} />
                <p style={{ marginTop: 12, color: "#888", fontSize: 13 }}>Loading monthly attendance…</p>
              </div>
            ) : filteredMonthlyStaff.length === 0 ? (
              <div className="sf-empty">
                <FiUsers size={40} color="#ddd" />
                <p>No staff found</p>
              </div>
            ) : (
              <>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#1a1a1a" }}>
                    {new Date(selectedMonth + "-01").toLocaleString("default", { month: "long", year: "numeric" })}
                    <span style={{ fontSize: 12, color: "#888", fontWeight: 400, marginLeft: 8 }}>
                      · {filteredMonthlyStaff.length} staff
                    </span>
                  </div>
                  {["SUPERADMIN", "ADMIN", "HR"].includes(myRole) && (
                    <div style={{ fontSize: 11, color: "#888" }}>
                      Click a cell to mark / edit attendance
                    </div>
                  )}
                </div>

                <div className="table-wrapper" style={{ overflowX: "auto" }}>
                  <table className="sf-table sf-attendance-grid">
                    <thead>
                      <tr>
                        <th className="sf-grid-sticky-col">Staff</th>
                        {daysInMonth.map((date) => {
                          const dayNum = parseInt(date.split("-")[2]);
                          const isSun  = isSunday(date);
                          const isToday = date === todayIST();
                          return (
                            <th
                              key={date}
                              style={{
                                textAlign: "center",
                                minWidth: 36,
                                color: isSun ? "#e53e3e" : isToday ? "#1d9e75" : "#888",
                                background: isToday ? "#E1F5EE" : "transparent",
                              }}
                            >
                              <div style={{ fontSize: 12, fontWeight: 800 }}>{String(dayNum).padStart(2, "0")}</div>
                              <div style={{ fontSize: 9, fontWeight: 600, textTransform: "uppercase" }}>{dayLabel(date)}</div>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredMonthlyStaff.map((staff) => (
                        <tr key={staff._id}>
                          <td className="sf-grid-sticky-col">
                            <div className="sf-member-cell">
                              <div className="sf-avatar" style={{ width: 28, height: 28, fontSize: 11 }}>
                                {staff.name?.charAt(0)?.toUpperCase()}
                              </div>
                              <div>
                                <div className="sf-member-name" style={{ fontSize: 12 }}>{staff.name}</div>
                                <div className="sf-member-email">{staff.role}</div>
                              </div>
                            </div>
                          </td>
                          {daysInMonth.map((date) => {
                            const rec      = staff.attendance?.[date];
                            const isSun    = isSunday(date);
                            const isFuture = date > todayIST();
                            const canEdit  = !isFuture && ["SUPERADMIN", "ADMIN", "HR"].includes(myRole);
                            const status   = rec?.status || (isSun ? "WEEK_OFF" : null);
                            const meta     = status ? STATUS_META[status] : null;

                            let title = "Not marked";
                            if (status) {
                              title = meta.label;
                              if (rec?.checkIn || rec?.checkOut) title += ` · ${rec.checkIn || "—"} - ${rec.checkOut || "—"}`;
                              if (rec?.leaveType) title += ` (${rec.leaveType})`;
                            } else if (isFuture) {
                              title = "Upcoming";
                            }

                            return (
                              <td
                                key={date}
                                title={title}
                                onClick={() => canEdit && openCellEdit(staff._id, staff.name, date)}
                                style={{
                                  textAlign: "center",
                                  cursor: canEdit ? "pointer" : "default",
                                  opacity: isFuture ? 0.35 : 1,
                                  background: date === todayIST() ? "#F4FBF8" : "transparent",
                                }}
                              >
                                {status ? (
                                  <span
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      width: 22,
                                      height: 22,
                                      borderRadius: 6,
                                      background: meta.bg,
                                      color: meta.color,
                                      fontSize: 10,
                                      fontWeight: 800,
                                    }}
                                  >
                                    {STATUS_SHORT[status] || status.charAt(0)}
                                  </span>
                                ) : isFuture ? (
                                  <span style={{ color: "#eee", fontSize: 12 }}>·</span>
                                ) : (
                                  <span
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      width: 22,
                                      height: 22,
                                      borderRadius: 6,
                                      background: "#FCEBEB",
                                      color: "#e53e3e",
                                      fontSize: 13,
                                      fontWeight: 800,
                                    }}
                                  >
                                    ?
                                  </span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          REPORT VIEW — all staff summary table
      ══════════════════════════════════════════════════════════════════ */}
      {viewMode === "report" && (
        <>
          <div className="card section">
            <div className="sf-toolbar">
              <div className="search-box">
                <FiSearch size={14} className="search-icon" />
                <input
                  placeholder="Search staff…"
                  value={reportSearch}
                  onChange={(e) => setReportSearch(e.target.value)}
                />
                {reportSearch && (
                  <button className="search-clear" onClick={() => setReportSearch("")}><FiX size={12} /></button>
                )}
              </div>
              <div className="sf-toolbar-right">
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <FiCalendar size={14} color="#888" />
                  <input
                    type="month"
                    value={reportMonth}
                    max={currentMonthIST()}
                    style={{ border: "1.5px solid #e5e7eb", borderRadius: 8, padding: "6px 10px", fontSize: 13, color: "#333" }}
                    onChange={(e) => setReportMonth(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="card section">
            {loading ? (
              <div className="sf-loading" style={{ height: "35vh" }}>
                <div className="spinner" style={{ width: 24, height: 24, borderColor: "#1d9e75", borderTopColor: "transparent" }} />
                <p style={{ marginTop: 12, color: "#888", fontSize: 13 }}>Loading report…</p>
              </div>
            ) : filteredReport.length === 0 ? (
              <div className="sf-empty">
                <FiBarChart2 size={40} color="#ddd" />
                <p>No report data for {reportMonth}</p>
              </div>
            ) : (
              <div className="table-wrapper">
                <table className="sf-table">
                  <thead>
                    <tr>
                      <th>Staff</th>
                      <th style={{ textAlign: "center" }}>Present</th>
                      <th style={{ textAlign: "center" }}>Absent</th>
                      <th style={{ textAlign: "center" }}>Half Day</th>
                      <th style={{ textAlign: "center" }}>Leave</th>
                      <th style={{ textAlign: "center" }}>LOP</th>
                      <th style={{ textAlign: "center" }}>Holiday</th>
                      <th style={{ textAlign: "center" }}>Work Hours</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredReport.map((r) => (
                      <tr key={r._id}>
                        <td>
                          <div className="sf-member-cell">
                            <div className="sf-avatar">{r.staffName?.charAt(0)?.toUpperCase()}</div>
                            <div>
                              <div className="sf-member-name">{r.staffName}</div>
                              <div className="sf-member-email">{r.staffRole} · {r.zoneId || "—"}</div>
                            </div>
                          </div>
                        </td>
                        {[
                          { val: r.present,  color: "#085041", bg: "#E1F5EE" },
                          { val: r.absent,   color: "#A32D2D", bg: "#FCEBEB" },
                          { val: r.halfDay,  color: "#633806", bg: "#FAEEDA" },
                          { val: r.leave,    color: "#3C3489", bg: "#EEEDFE" },
                          { val: r.lop,      color: "#A32D2D", bg: "#FCEBEB" },
                          { val: r.holiday,  color: "#0C447C", bg: "#E6F1FB" },
                        ].map(({ val, color, bg }, i) => (
                          <td key={i} style={{ textAlign: "center" }}>
                            {val > 0
                              ? <span style={{ fontWeight: 700, fontSize: 13, color, background: bg, borderRadius: 6, padding: "2px 10px" }}>{val}</span>
                              : <span style={{ color: "#ccc", fontSize: 13 }}>—</span>}
                          </td>
                        ))}
                        <td style={{ textAlign: "center" }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: "#0C447C" }}>
                            {r.totalWorkHours > 0 ? `${r.totalWorkHours.toFixed(1)}h` : "—"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          BULK MARK MODAL
      ══════════════════════════════════════════════════════════════════ */}
      {bulkMode && (
        <div className="modal-overlay" onClick={() => setBulkMode(false)}>
          <div className="modal-content sf-modal-wide" style={{ maxWidth: 780 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <FiEdit2 size={15} style={{ marginRight: 7 }} />
                Bulk Mark Attendance — {selectedDate}
              </h3>
              <button className="modal-close" onClick={() => setBulkMode(false)}><FiX size={18} /></button>
            </div>

            <div className="modal-body" style={{ padding: "0 0 4px" }}>
              {/* Search inside modal */}
              <div style={{ padding: "14px 24px 0" }}>
                <div className="search-box">
                  <FiSearch size={14} className="search-icon" />
                  <input
                    placeholder="Filter staff…"
                    value={staffSearch}
                    onChange={(e) => {
                      setStaffSearch(e.target.value);
                      clearTimeout(debounceRef.current);
                      debounceRef.current = setTimeout(() => fetchStaff(e.target.value), 350);
                    }}
                  />
                  {staffSearch && <button className="search-clear" onClick={() => { setStaffSearch(""); fetchStaff(""); }}><FiX size={12} /></button>}
                </div>
              </div>

              {/* Quick-set all buttons */}
              <div style={{ padding: "12px 24px", display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <span style={{ fontSize: 12, color: "#888", fontWeight: 600 }}>Set all to:</span>
                {STATUSES.map((s) => {
                  const m = STATUS_META[s];
                  return (
                    <button
                      key={s}
                      style={{ fontSize: 11, padding: "4px 10px", borderRadius: 6, border: `1.5px solid ${m.dot}`, background: m.bg, color: m.color, fontWeight: 700, cursor: "pointer" }}
                      onClick={() => {
                        const updated = {};
                        staffList.forEach((st) => {
                          updated[st._id] = { ...bulkForm[st._id], status: s };
                        });
                        setBulkForm((f) => ({ ...f, ...updated }));
                      }}
                    >
                      {m.label}
                    </button>
                  );
                })}
              </div>

              {/* Staff rows */}
              <div style={{ maxHeight: 420, overflowY: "auto", borderTop: "1.5px solid #f3f4f6" }}>
                {staffList.length === 0 ? (
                  <div className="sf-empty" style={{ padding: "30px 0" }}>
                    <FiUsers size={32} color="#ddd" />
                    <p style={{ fontSize: 13 }}>No staff found</p>
                  </div>
                ) : (
                  staffList.map((s, idx) => {
                    const row = bulkForm[s._id] || { status: "PRESENT", checkIn: "", checkOut: "", leaveType: "" };
                    const m   = STATUS_META[row.status] || STATUS_META.PRESENT;
                    return (
                      <div
                        key={s._id}
                        style={{ display: "grid", gridTemplateColumns: "1fr 140px 90px 90px 110px", gap: 8, alignItems: "center", padding: "10px 24px", borderBottom: "1px solid #f9fafb", background: idx % 2 === 0 ? "#fff" : "#fafafa" }}
                      >
                        {/* Staff info */}
                        <div className="sf-member-cell" style={{ gap: 8 }}>
                          <div className="sf-avatar" style={{ width: 30, height: 30, fontSize: 11 }}>{s.name?.charAt(0)}</div>
                          <div>
                            <div className="sf-member-name" style={{ fontSize: 12 }}>{s.name}</div>
                            <div className="sf-member-email">{s.role}</div>
                          </div>
                        </div>

                        {/* Status select */}
                        <select
                          value={row.status}
                          style={{ fontSize: 12, padding: "5px 8px", borderRadius: 7, border: `1.5px solid ${m.dot}`, background: m.bg, color: m.color, fontWeight: 700 }}
                          onChange={(e) => {
                            const newStatus = e.target.value;
                            const newLeaveType =
                              newStatus === "LEAVE"
                                ? resolveLeaveType(bulkAttMap[s._id] || {}, selectedDate)
                                : (bulkForm[s._id]?.leaveType || "");
                            setBulkForm((f) => ({ ...f, [s._id]: { ...f[s._id], status: newStatus, leaveType: newLeaveType } }));
                          }}
                        >
                          {STATUSES.map((st) => <option key={st} value={st}>{STATUS_META[st].label}</option>)}
                        </select>

                        {/* Check In */}
                        <input
                          type="time"
                          value={row.checkIn || ""}
                          style={{ fontSize: 12, padding: "5px 6px", borderRadius: 7, border: "1.5px solid #e5e7eb", color: "#333" }}
                          disabled={["ABSENT","LEAVE","HOLIDAY","WEEK_OFF"].includes(row.status)}
                          onChange={(e) => setBulkForm((f) => ({ ...f, [s._id]: { ...f[s._id], checkIn: e.target.value } }))}
                        />

                        {/* Check Out */}
                        <input
                          type="time"
                          value={row.checkOut || ""}
                          style={{ fontSize: 12, padding: "5px 6px", borderRadius: 7, border: "1.5px solid #e5e7eb", color: "#333" }}
                          disabled={["ABSENT","LEAVE","HOLIDAY","WEEK_OFF"].includes(row.status)}
                          onChange={(e) => setBulkForm((f) => ({ ...f, [s._id]: { ...f[s._id], checkOut: e.target.value } }))}
                        />

                        {/* Leave type — auto-resolved, allow override */}
                        {row.status === "LEAVE" ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                            <select
                              value={row.leaveType || ""}
                              style={{
                                fontSize: 12, padding: "5px 8px", borderRadius: 7,
                                border: `1.5px solid ${row.leaveType === "LOP" ? "#e53e3e" : "#1d9e75"}`,
                                background: row.leaveType === "LOP" ? "#FCEBEB" : "#E1F5EE",
                                color: row.leaveType === "LOP" ? "#A32D2D" : "#085041",
                                fontWeight: 700,
                              }}
                              onChange={(e) => setBulkForm((f) => ({ ...f, [s._id]: { ...f[s._id], leaveType: e.target.value } }))}
                            >
                              <option value="">Type…</option>
                              {LEAVE_TYPES.map((lt) => <option key={lt} value={lt}>{lt}</option>)}
                            </select>
                            {row.leaveType === "LOP" && (
                              <span style={{ fontSize: 10, color: "#A32D2D" }}>Casual used ↑</span>
                            )}
                          </div>
                        ) : (
                          <div style={{ color: "#ddd", fontSize: 12, textAlign: "center" }}>—</div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            <div className="modal-actions">
              <span style={{ fontSize: 12, color: "#888" }}>{staffList.length} staff</span>
              <div style={{ display: "flex", gap: 10 }}>
                <button className="btn-outline-green" onClick={() => setBulkMode(false)}>Cancel</button>
                <button className="btn btn-success" disabled={submitting} onClick={handleBulkSubmit}>
                  {submitting ? (
                    <><div className="spinner" style={{ width: 14, height: 14 }} /> Saving…</>
                  ) : (
                    <><FiSave size={14} /> Save Attendance</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          CELL EDIT MODAL (monthly view click)
      ══════════════════════════════════════════════════════════════════ */}
      {editingCell && (
        <div className="modal-overlay" onClick={() => setEditingCell(null)}>
          <div
            className="modal-content"
            style={{ maxWidth: 420, height: "auto", minHeight: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>
                <FiCalendar size={14} style={{ marginRight: 7 }} />
                {editingCell.staffName} — {editingCell.date} ({dayLabel(editingCell.date)})
              </h3>
              <button className="modal-close" onClick={() => setEditingCell(null)}><FiX size={18} /></button>
            </div>

            <div className="modal-body">
              {/* Status */}
              <div className="field">
                <label>Status</label>
                <select
                  value={cellForm.status}
                  onChange={(e) => {
                    const newStatus = e.target.value;
                    // Auto-set leaveType when switching to LEAVE
                    const newLeaveType =
                      newStatus === "LEAVE"
                        ? resolveLeaveType(cellForm._attMap || {}, editingCell?.date)
                        : cellForm.leaveType;
                    setCellForm((f) => ({ ...f, status: newStatus, leaveType: newLeaveType }));
                  }}
                  style={{ fontWeight: 600, color: STATUS_META[cellForm.status]?.color }}
                >
                  {STATUSES.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
                </select>
              </div>

              {/* Auto-resolved leave info banner */}
              {cellForm.status === "LEAVE" && (
                <div style={{
                  marginTop: 10, padding: "8px 12px", borderRadius: 8, fontSize: 12, fontWeight: 600,
                  background: cellForm.leaveType === "LOP" ? "#FCEBEB" : "#E1F5EE",
                  color: cellForm.leaveType === "LOP" ? "#A32D2D" : "#085041",
                }}>
                  {cellForm.leaveType === "LOP"
                    ? "⚠ Casual leave already used this month — marking as LOP"
                    : "✓ Casual leave available — marking as Casual"}
                </div>
              )}

              {/* Check in / out */}
              {["PRESENT", "HALF_DAY"].includes(cellForm.status) && (
                <div className="sf-two-col" style={{ marginTop: 14 }}>
                  <div className="field">
                    <label><FiClock size={12} style={{ marginRight: 5 }} />Check In</label>
                    <input type="time" value={cellForm.checkIn} onChange={(e) => setCellForm((f) => ({ ...f, checkIn: e.target.value }))} />
                  </div>
                  <div className="field">
                    <label><FiClock size={12} style={{ marginRight: 5 }} />Check Out</label>
                    <input type="time" value={cellForm.checkOut} onChange={(e) => setCellForm((f) => ({ ...f, checkOut: e.target.value }))} />
                  </div>
                </div>
              )}

              {/* Leave type — auto-resolved, but HR can override manually */}
              {cellForm.status === "LEAVE" && (
                <div className="field" style={{ marginTop: 14 }}>
                  <label>Leave Type <span style={{ fontWeight: 400, color: "#aaa", fontSize: 11 }}>(auto-decided, can override)</span></label>
                  <select value={cellForm.leaveType} onChange={(e) => setCellForm((f) => ({ ...f, leaveType: e.target.value }))}>
                    <option value="">— Select —</option>
                    {LEAVE_TYPES.map((lt) => <option key={lt} value={lt}>{lt}</option>)}
                  </select>
                </div>
              )}

              {/* Notes */}
              <div className="field" style={{ marginTop: 14 }}>
                <label>Notes (optional)</label>
                <input placeholder="e.g. Late arrival, medical leave…" value={cellForm.notes} onChange={(e) => setCellForm((f) => ({ ...f, notes: e.target.value }))} />
              </div>

              {/* Work hours preview */}
              {cellForm.checkIn && cellForm.checkOut && (() => {
                const [ih, im] = cellForm.checkIn.split(":").map(Number);
                const [oh, om] = cellForm.checkOut.split(":").map(Number);
                const diff = (oh * 60 + om) - (ih * 60 + im);
                const hrs  = diff > 0 ? (diff / 60).toFixed(2) : 0;
                return hrs > 0 ? (
                  <div style={{ marginTop: 12, padding: "8px 14px", background: "#E1F5EE", borderRadius: 8, fontSize: 12, color: "#085041", fontWeight: 600 }}>
                    ⏱ Work hours: {hrs}h
                  </div>
                ) : null;
              })()}
            </div>

            <div className="modal-actions">
              <button className="btn-outline-green" onClick={() => setEditingCell(null)}>Cancel</button>
              <button className="btn btn-success" disabled={submitting} onClick={handleCellSave}>
                {submitting ? (
                  <><div className="spinner" style={{ width: 14, height: 14 }} /> Saving…</>
                ) : (
                  <><FiSave size={14} /> Save</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}